/**
 * Editable fields in widget HTML: text, links and images marked with
 * data-gx-field="name" (the AI brief asks for these), or detected and marked
 * on request. Lets people change copy without touching code.
 *
 * HTML is parsed in an inert <template> (nothing runs, images don't load) and
 * written back from it. The live widget sanitizes and re-serializes HTML the
 * same way, so this doesn't change what renders.
 */

export type FieldType = 'text' | 'richtext' | 'link' | 'image';

export type WidgetField = {
  /** Unique key: the name, plus "#n" when several elements share it. */
  key: string;
  name: string;
  index: number;
  type: FieldType;
  /** Text (or inner HTML for richtext); for links, the link text. */
  value: string;
  href?: string;
  src?: string;
  alt?: string;
};

export type FieldPatch = { value?: string; href?: string; src?: string; alt?: string };

const INLINE_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'BR', 'SPAN', 'SMALL', 'SUP', 'SUB', 'U', 'MARK', 'S', 'CODE', 'ABBR']);
const TEXT_TAGS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'LI', 'BLOCKQUOTE', 'FIGCAPTION', 'DT', 'DD'];

const parse = (html: string): HTMLTemplateElement => {
  const t = document.createElement('template');
  t.innerHTML = html;
  return t;
};

const onlyInline = (el: Element): boolean => Array.from(el.children).every(c => INLINE_TAGS.has(c.tagName) && onlyInline(c));

const typeOf = (el: Element): FieldType | null => {
  const declared = (el.getAttribute('data-gx-type') || '').toLowerCase();
  if (['text', 'richtext', 'link', 'image'].includes(declared)) return declared as FieldType;
  if (el.tagName === 'IMG') return 'image';
  if (el.tagName === 'A') return onlyInline(el) ? 'link' : null;
  if (!el.children.length) return 'text';
  return onlyInline(el) ? 'richtext' : null;
};

const readField = (el: Element, type: FieldType): Omit<WidgetField, 'key' | 'name' | 'index'> => {
  if (type === 'image') return { type, value: '', src: el.getAttribute('src') || '', alt: el.getAttribute('alt') || '' };
  if (type === 'link') return { type, value: el.textContent || '', href: el.getAttribute('href') || '' };
  if (type === 'richtext') return { type, value: el.innerHTML.trim() };
  return { type, value: el.textContent || '' };
};

/** Fields marked with data-gx-field, in page order. Elements that can't be edited as a field are skipped. */
export const listFields = (html: string): WidgetField[] => {
  const t = parse(html);
  const counts: Record<string, number> = {};
  const all = Array.from(t.content.querySelectorAll('[data-gx-field]'));
  all.forEach((el) => { const n = el.getAttribute('data-gx-field') || ''; counts[n] = (counts[n] || 0) + 1; });
  const seen: Record<string, number> = {};
  const fields: WidgetField[] = [];
  all.forEach((el) => {
    const name = (el.getAttribute('data-gx-field') || '').trim();
    if (!name) return;
    const index = seen[name] || 0;
    seen[name] = index + 1;
    const type = typeOf(el);
    if (!type) return;
    fields.push({ key: counts[name] > 1 ? `${name}#${index + 1}` : name, name, index, ...readField(el, type) });
  });
  return fields;
};

/** Applies a change to one field (by key) and returns the new HTML. */
export const updateField = (html: string, key: string, patch: FieldPatch): string => {
  const t = parse(html);
  const [name, n] = key.split('#');
  const matches = Array.from(t.content.querySelectorAll('[data-gx-field]')).filter(el => el.getAttribute('data-gx-field') === name);
  const el = matches[n ? Number(n) - 1 : 0];
  if (!el) return html;
  const type = typeOf(el);
  if (type === 'image') {
    if (patch.src !== undefined) el.setAttribute('src', patch.src);
    if (patch.alt !== undefined) el.setAttribute('alt', patch.alt);
  } else if (type === 'link') {
    if (patch.value !== undefined) el.textContent = patch.value;
    if (patch.href !== undefined) el.setAttribute('href', patch.href);
  } else if (type === 'richtext') {
    if (patch.value !== undefined) el.innerHTML = patch.value;
  } else if (type === 'text') {
    if (patch.value !== undefined) el.textContent = patch.value;
  }
  return t.innerHTML;
};

type Candidate = { el: Element; type: FieldType; base: string };

const findCandidates = (root: DocumentFragment): Candidate[] => {
  const out: Candidate[] = [];
  root.querySelectorAll('img, a, button, h1, h2, h3, h4, h5, h6, p, li, blockquote, figcaption').forEach((el) => {
    if (el.hasAttribute('data-gx-field') || el.closest('[data-gx-field]')) return;
    if (el.tagName === 'IMG') { out.push({ el, type: 'image', base: 'image' }); return; }
    const text = (el.textContent || '').trim();
    if (!text) return;
    if (el.tagName === 'A' || el.tagName === 'BUTTON') {
      if (onlyInline(el)) out.push({ el, type: el.tagName === 'A' ? 'link' : 'text', base: el.tagName === 'A' ? 'link' : 'button' });
      return;
    }
    if (!TEXT_TAGS.includes(el.tagName) || !onlyInline(el)) return;
    // Text that sits inside another candidate (a <p> in a <li>) is edited with its parent.
    if (out.some(c => c.el.contains(el))) return;
    out.push({ el, type: el.children.length ? 'richtext' : 'text', base: /^H\d$/.test(el.tagName) ? 'heading' : el.tagName === 'LI' ? 'item' : 'text' });
  });
  return out;
};

/** How many elements "Make text editable" would mark. */
export const countFieldCandidates = (html: string): number => findCandidates(parse(html).content).length;

/** Marks detected text, links, buttons and images with data-gx-field names. */
export const addFieldMarkers = (html: string): string => {
  const t = parse(html);
  const used = new Set(Array.from(t.content.querySelectorAll('[data-gx-field]')).map(el => el.getAttribute('data-gx-field')));
  const next: Record<string, number> = {};
  findCandidates(t.content).forEach(({ el, base }) => {
    let name;
    do { next[base] = (next[base] || 0) + 1; name = `${base}-${next[base]}`; } while (used.has(name));
    used.add(name);
    el.setAttribute('data-gx-field', name);
  });
  return t.innerHTML;
};
